import { capitalize } from '@mui/material';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { customFetch } from '~/utils/customFetch';
import { ErrorObjectString } from '~/utils/typescriptHelpers';
import { useAccountParams } from '../../useAccountParams';
import { Audience, Cohort } from '../useAdmanagerData/types';

export type CohortType = {
  ownerClientId: number | null;
  cohorts: Array<Cohort>;
  audiences: Array<Audience>;
  ownerLogo: string;
  ownerTitleText: string;
  id: number;
  name: string;
};

type ResponseData = Array<CohortType> | ErrorObjectString;

async function getCohorts({ accountId }: { accountId?: number | string }) {
  const { data } = await customFetch.get<ResponseData>(
    `/authenticated/client/${accountId}/cohorts`,
    {
      headers: {
        'content-type': 'application/json',
      },
      useNodeEndpoint: true,
    },
  );
  if ('error' in data) {
    throw new Error(data.error);
  }

  const processedData = data.map((cohortType) => ({
    ...cohortType,
    name: capitalize(cohortType.name),
    ownerTitleText: capitalize(cohortType.ownerTitleText),
    audiences: cohortType.audiences.map((audience) => {
      return {
        ...audience,
        name: audience.name.replace(/\s?\[.*?\]$/, ''),
      };
    }),
  }));

  return processedData;
}

export type ProcessedCohortType = Awaited<ReturnType<typeof getCohorts>>;

export function getCohortsQueryOptions(accountId?: number | string, hasPreferences = false) {
  return {
    queryKey: ['cohorts', String(accountId), hasPreferences], // hasPreferences key enables accessing cached preferences
    queryFn: async () => getCohorts({ accountId }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 30, // 30 minutes
  };
}

export function useCohortQuery({
  hasPreferences,
}: {
  hasPreferences?: boolean;
}): UseQueryResult<ProcessedCohortType>;
export function useCohortQuery<TData = ProcessedCohortType>({
  hasPreferences,
  select,
}: {
  hasPreferences?: boolean;
  select: (data: ProcessedCohortType) => TData;
}): UseQueryResult<TData>;
export function useCohortQuery<TData = ProcessedCohortType>({
  hasPreferences = false,
  select,
}: {
  hasPreferences?: boolean;
  select?: (data: ProcessedCohortType) => TData;
}): UseQueryResult<TData> {
  const accountParams = useAccountParams();
  return useQuery({
    ...getCohortsQueryOptions(accountParams?.accountId, hasPreferences),
    select,
  });
}

export default useCohortQuery;
